import { EyeInvisibleOutlined, EyeOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { Space, Table, TableColumnProps, TableProps, Tag, Tooltip, Typography } from 'antd';
import moment from 'moment';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { JobPostingCollection } from '../../types';
import { calculateOpenDays } from '../../utils';
import styles from './OpportunityTable.module.css';

interface Props extends TableProps<JobPostingCollection> {
  actionsRender: NonNullable<TableColumnProps<JobPostingCollection>['render']>;
}

const OpportunityTable: React.FC<Props> = ({ actionsRender, ...props }) => {
  const { t } = useTranslation();

  return (
    <Table rowKey="id" {...props}>
      <Table.Column
        title={t('Vagas')}
        width="40%"
        render={(_value, record: JobPostingCollection) => (
          <>
            <Space direction="horizontal">
              <Link to={`${record.id}`} className={`ant-typography-link ${styles.breakWord}`}>
                {`${record.id} - ${record.name}`}
              </Link>
              {record.status === 'IN_PROGRESS' ? (
                record.external_publication &&
                moment().isBetween(
                  moment(record.external_publication_start_at as string),
                  moment(record.external_publication_end_at as string),
                  undefined,
                  '[]'
                ) ? (
                  <Tooltip title={t('Publicada')}>
                    <EyeOutlined className={styles.successColor} />
                  </Tooltip>
                ) : record.unlisted_external_publication ? (
                  <Tooltip title={t('Não listada')}>
                    <FolderOpenOutlined className={styles.unlistedColor} />
                  </Tooltip>
                ) : (
                  <Tooltip title={t('Não Publicada')}>
                    <EyeInvisibleOutlined />
                  </Tooltip>
                )
              ) : null}
            </Space>
            <br />
            <Typography.Text type="secondary">
              {`${t('Dias em aberto')}: ${calculateOpenDays(
                record.created_at,
                record.finalization_date
              )}`}
            </Typography.Text>
            {record.request ? (
              <Typography.Text style={{ color: '#3c6b8a' }}>
                {' '}
                <Trans>ID da requisição:</Trans> {record.request}
              </Typography.Text>
            ) : (
              <></>
            )}
          </>
        )}
      />
      <Table.Column
        title={t('Status da vaga')}
        render={(_, record: JobPostingCollection) => (
          <>
            {record.status === 'IN_PROGRESS' &&
              ((record.limit_days && calculateOpenDays(record.created_at) <= record.limit_days) ||
                record.limit_days === null) && (
                <Tag color="blue">
                  <Trans>Em andamento</Trans>
                </Tag>
              )}
            {record.status === 'IN_PROGRESS' &&
              record.limit_days &&
              calculateOpenDays(record.created_at) > record.limit_days && (
                <Tag color="error">
                  <Trans>Atrasada</Trans>
                </Tag>
              )}
            {record.status === 'SUSPENDED' && (
              <Tag>
                <Trans>Congelada</Trans>
              </Tag>
            )}
            {record.status === 'CANCELED' && (
              <Tag>
                <Trans>Cancelada</Trans>
              </Tag>
            )}
            {record.status === 'FINALIZED' && (
              <Tag color="success">
                <Trans>Concluída</Trans>
              </Tag>
            )}
          </>
        )}
      />
      <Table.Column
        title={t('Funil atual')}
        colSpan={4}
        align="center"
        dataIndex="id"
        render={(_value, record: JobPostingCollection) => (
          <>
            <Typography.Text>{record.active_subscribers_count}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              <Trans>Inscritos ativos</Trans>
            </Typography.Text>
          </>
        )}
      />
      <Table.Column
        colSpan={0}
        align="center"
        dataIndex="id"
        render={(_id, record: JobPostingCollection) => (
          <>
            <Typography.Text>{record.in_progress_count}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              <Trans>Em&nbsp;processo</Trans>
            </Typography.Text>
          </>
        )}
      />
      <Table.Column
        colSpan={0}
        align="center"
        render={(_value, record: JobPostingCollection) => (
          <>
            <Typography.Text>
              {record.filled_positions}/{record.positions}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary">
              <Trans>Contratados</Trans>
            </Typography.Text>
          </>
        )}
      />
      <Table.Column
        colSpan={0}
        align="center"
        dataIndex="id"
        render={(_id, record: JobPostingCollection) => (
          <>
            <Typography.Text type="danger">{record.inactive_count}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              <Trans>Reprovados</Trans>
            </Typography.Text>
          </>
        )}
      />
      <Table.Column title={t('Ações')} align="center" render={actionsRender} />
    </Table>
  );
};
export default OpportunityTable;
